import { InboxOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Flex,
  Spin,
  Table,
  Typography,
  Upload,
  message,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { previewOperationPlanImport, runOperationPlanImport } from '../services/agents';
import { formatPlanRatio } from '../utils/plan-values';

/* global BigInt */

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const SUBJECT_KIND = {
  root: '总部',
  region: '区总',
  store: '门店',
};

const formatAmountPlanValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  try {
    const amountCent = BigInt(value);
    const absolute = amountCent < 0n ? -amountCent : amountCent;
    const whole = absolute / 1000000n;
    const fraction = String(absolute % 1000000n)
      .padStart(6, '0')
      .replace(/0+$/, '');
    return `${amountCent < 0n ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''} 万元`;
  } catch {
    return '-';
  }
};

const formatPlanValue = ({ valueType, amountCent, ratio }) =>
  valueType === 'ratio' ? formatPlanRatio(ratio) : formatAmountPlanValue(amountCent);

const targetColumns = [
  { title: '层级', dataIndex: 'kind', render: (value) => SUBJECT_KIND[value] || value },
  { title: '编码', dataIndex: 'code', render: (value) => value || '-' },
  { title: '名称', dataIndex: 'name' },
  { title: '计划项目', dataIndex: 'planItemName' },
  { title: '计划值', dataIndex: 'planValue', render: (_, record) => formatPlanValue(record) },
];

const issueColumns = [
  { title: '工作表', dataIndex: 'sheetName' },
  { title: '行', dataIndex: 'rowNumber', width: 72 },
  { title: '编码', dataIndex: 'code', render: (value) => value || '-' },
  { title: '名称', dataIndex: 'name', render: (value) => value || '-' },
  { title: '问题', dataIndex: 'message' },
];

const withKeys = (rows = []) => rows.map((row, index) => ({ ...row, key: `${index}` }));

const AgentOperationPlanImport = ({ onCompleted, organizationId }) => {
  const [file, setFile] = useState();
  const [preview, setPreview] = useState();
  const [planMonth, setPlanMonth] = useState();
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const previewRequestRef = useRef(0);

  const resetPreview = () => {
    setPreview(undefined);
    setPlanMonth(undefined);
  };

  const selectFile = (nextFile) => {
    if (!/\.(?:xls|xlsx)$/i.test(nextFile.name)) {
      message.error('仅支持 .xls 和 .xlsx 文件');
      return Upload.LIST_IGNORE;
    }
    if (nextFile.size > MAX_FILE_SIZE) {
      message.error('Excel 文件不能超过 5 MiB');
      return Upload.LIST_IGNORE;
    }
    setFile(nextFile);
    handlePreview(nextFile);
    return false;
  };

  const handlePreview = async (nextFile) => {
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    resetPreview();
    setPreviewing(true);
    try {
      const result = await previewOperationPlanImport({ file: nextFile, organizationId });
      if (previewRequestRef.current !== requestId) {
        return;
      }
      const data = result?.data || result;
      setPreview(data);
      setPlanMonth(dayjs(`${data.suggestedPlanMonth}-01`));
    } catch {
      if (previewRequestRef.current === requestId) {
        resetPreview();
      }
    } finally {
      if (previewRequestRef.current === requestId) {
        setPreviewing(false);
      }
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await runOperationPlanImport({
        file,
        organizationId,
        planMonth: planMonth.format('YYYY-MM'),
      });
      message.success('经营计划导入已提交');
      onCompleted();
    } finally {
      setSubmitting(false);
    }
  };

  const summaryItems = preview
    ? [
        { key: 'file', label: '文件', children: preview.fileName },
        { key: 'month', label: '计划月份', children: preview.suggestedPlanMonth },
        { key: 'subjects', label: '模板主体', children: preview.subjectCount },
        { key: 'targets', label: '计划目标', children: preview.targetCount },
      ]
    : [];

  return (
    <Flex vertical gap="large">
      <Upload.Dragger
        accept=".xls,.xlsx"
        beforeUpload={selectFile}
        disabled={previewing || submitting}
        fileList={file ? [file] : []}
        maxCount={1}
        onRemove={() => {
          previewRequestRef.current += 1;
          setPreviewing(false);
          setFile(undefined);
          resetPreview();
        }}
      >
        <Flex vertical align="center" gap="small">
          <InboxOutlined />
          <Typography.Text>Excel 经营计划文件</Typography.Text>
        </Flex>
      </Upload.Dragger>

      {previewing ? (
        <Flex role="status" aria-live="polite" align="center" justify="end" gap="small">
          <Spin size="small" />
          <Typography.Text type="secondary">正在预检</Typography.Text>
        </Flex>
      ) : null}

      {preview ? (
        <Flex vertical gap="large">
          <Descriptions bordered size="small" items={summaryItems} />
          {preview.valid ? (
            <Alert type="success" showIcon title="预检通过" />
          ) : (
            <Alert type="error" showIcon title={`预检发现 ${preview.issues.length} 个问题`} />
          )}
          {preview.issues.length > 0 ? (
            <Table
              rowKey="key"
              size="small"
              columns={issueColumns}
              dataSource={withKeys(preview.issues)}
              pagination={{ pageSize: 10 }}
            />
          ) : (
            <Table
              rowKey="key"
              size="small"
              columns={targetColumns}
              dataSource={withKeys(preview.targets)}
              pagination={{ pageSize: 10 }}
            />
          )}
          <Flex align="center" justify="end" gap="middle">
            <DatePicker
              picker="month"
              allowClear={false}
              format="YYYY-MM"
              value={planMonth}
              onChange={setPlanMonth}
            />
            <Button
              type="primary"
              disabled={!preview.valid || !planMonth}
              loading={submitting}
              onClick={handleSubmit}
            >
              确认导入
            </Button>
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  );
};

AgentOperationPlanImport.propTypes = {
  onCompleted: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
};

export default AgentOperationPlanImport;
export { formatPlanValue };
